import {
    Alert,
    Box,
    Modal,
    ModalFooter,
    ThemeShadowVariable,
    ThemeSpaceVariable,
    useDisclosure,
} from "@caspeco/casper-ui-library";
import { useCancelBooking } from "api/api-hooks/use-cancel-booking";
import { useCreateBooking } from "api/api-hooks/use-create-booking";
import ConfirmationDialog from "components/confirm-dialog";
import ContinueButton from "components/selection-components/customize-wizard/continue-button";
import CustomizeWizardModalBody from "components/selection-components/customize-wizard/customize-wizard-modal-body-wrapper";
import PreorderBody from "components/selection-components/preorder/preorder-body";
import PreorderContinueButton from "components/selection-components/preorder/preorder-continue-button";
import ActivityModalBody from "components/selection-components/select-activity/activity-modal-body";
import { useOriginalSelectedTime } from "components/selection-components/select-activity/use-original-selected-time";
import { useRemoveActivity } from "components/selection-components/summary/use-remove-activity";
import { ActivityContext } from "context/activity-context";
import { PreorderContext } from "context/preorder-context";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { useDurationFormatter } from "hooks/use-duration-formatter";
import { saveSectionNameAndWebRuleTitle } from "logger/event-utils";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import queryClient from "query-client";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { urlBuilder } from "search-params/url-builder";
import { Time } from "store/store-types";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { BookingMenu, MenuStatus } from "types/menu";
import testId from "types/test-id";
import { TimeSlot } from "types/time-slot";
// eslint-disable-next-line caspeco/discourage-chakra-import
import { useBreakpointValue } from "@chakra-ui/react";
import { useBookingMenuRespond } from "api/api-hooks/use-booking-menu-respond";
import { useUpdateBooking } from "api/api-hooks/use-update-booking";
import { useWebBooking } from "api/api-hooks/use-web-booking";

interface ICustomizeActiv {
    setSummaryOpen: (open: boolean) => void;
}

const CustomizeActivity = ({ setSummaryOpen }: ICustomizeActiv) => {
    const { t } = useTranslation();
    const activityContext = useContext(ActivityContext);
    const { openActivity, setOpenActivity } = activityContext || {};
    const { bookingId } = useParams();
    const state = useBookingStateContext();
    const { handleCreateBooking, setState } = useBookingActionsContext();
    const [step, setStep] = useState(0);
    const originalTime = useOriginalSelectedTime();
    const { data: booking } = useWebBooking();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [selectedTime, setSelectedTime] = useState<TimeSlot>();
    const [selectedWaitListTimes, setSelectedWaitListTimes] = useState<TimeSlot[]>([]);
    const modalSize = useBreakpointValue({
        base: "full",
        md: "md",
    }) as "md" | "full";
    const { guests, isBookingRequest } = useBookingStateContext();
    const maxGroupSize = useMemo(() => {
        const maxSizes = openActivity?.timeSets.map((timeSet) => timeSet.maxGroupSize) || [];
        return Math.max(0, ...maxSizes);
    }, [openActivity]);
    const preorderContext = useContext(PreorderContext);
    const { currentQuantities, currentTotalQuantity, setSelectedArticleHasMandatoryMenu } =
        preorderContext || {};

    // Track activity modal when opened
    useStepTracking({
        stepName: TrackingStepName.ActivityModal,
        metadata: {
            activityName: openActivity?.name,
        },
        autoTrack: !!openActivity,
        dependencies: [openActivity?.id],
    });

    const handleSelectTime = (time: TimeSlot) => {
        if (selectedWaitListTimes.length > 0) setSelectedWaitListTimes([]);
        setState?.((prevState) => {
            return {
                ...prevState,
                isWaitList: false,
            };
        });

        if (selectedTime && time?.start.isSame(selectedTime?.start)) {
            setSelectedTime(undefined);
        } else {
            setSelectedTime(time);
        }
    };

    useEffect(() => {
        if (booking && originalTime) {
            setSelectedTime(originalTime);
        }
    }, [originalTime, booking, openActivity]);

    const setWaitListTime = (time: TimeSlot) => {
        if (selectedTime) setSelectedTime(undefined);
        setState?.((prevState) => ({
            ...prevState,
            isWaitList: true,
        }));
        const timeIndex = selectedWaitListTimes.indexOf(time);

        let newSelectedTimes: TimeSlot[];

        if (timeIndex > -1) {
            newSelectedTimes = selectedWaitListTimes.filter((_, index) => index !== timeIndex);
        } else {
            newSelectedTimes = [...selectedWaitListTimes, time];
        }

        setSelectedWaitListTimes(newSelectedTimes);
    };

    const handleWaitlistContinue = (skip = false) => {
        if (booking?.articles && booking.articles.length > 0 && !skip) {
            onOpen();
            return;
        }
        const stateWithTimes = {
            ...state,
            times: selectedWaitListTimes as Time[],
            isWaitList: true,
        };
        handleCreateBooking?.(stateWithTimes);
        onCloseModal();
        setSummaryOpen(false);
    };
    const onCloseModal = () => {
        onClose();
        setOpenActivity?.(null);
        setSelectedTime(undefined);
    };

    const { removeActivity } = useRemoveActivity(onCloseModal, onCloseModal);

    const createBooking = useCreateBooking();
    const updateBooking = useUpdateBooking();
    const formatter = useDurationFormatter();

    const shouldRemoveActivity = originalTime && !selectedTime;

    const duration = () => {
        if (!selectedTime) return "";
        return formatter(
            selectedTime.start,
            selectedTime.end.clone().subtract(selectedTime.recoupTime, "minute")
        );
    };

    const activityButtonText = () => {
        if (selectedWaitListTimes.length > 0) {
            return t("waitList.continueWithSelectedTimes");
        }

        if (shouldRemoveActivity) {
            return t("removeActivityX", { activityName: openActivity?.name });
        }
        if (!selectedTime) {
            return t("addWithEllipsis");
        }

        return `${t("continueWithTimeX", {
            time: selectedTime?.start.format("HH:mm"),
        })} (${duration()})`;
    };

    const onActivityModalContinue = async () => {
        if (shouldRemoveActivity && openActivity?.id) {
            return await removeActivity(originalTime.ruleId, openActivity.id);
        }

        if (!selectedTime) {
            return;
        }

        const newTime: Time = {
            ruleId: selectedTime.ruleId,
            start: selectedTime.start,
        };
        saveSectionNameAndWebRuleTitle(selectedTime.name, selectedTime.title);
        const stateWithTimes = {
            ...state,
            times: [newTime] as Time[],
        };
        setState?.(stateWithTimes);

        let bookingResult;
        if (booking) {
            bookingResult = await updateBooking.mutateAsync({
                state: stateWithTimes,
                timesToUpdate: [newTime],
                isInquiry: isBookingRequest,
            });
        } else {
            bookingResult = await createBooking.mutateAsync({
                state: stateWithTimes,
                isInquiry: isBookingRequest,
            });
        }
        const updatedArticle = bookingResult?.articles?.find((article) => {
            return selectedTime.start.isSame(article.start);
        });

        /**
         * If the article has a menu we show the preorder step, otherwise we reset the step and close the modal.
         */
        const articeHasMenu = updatedArticle?.menuGuid && updatedArticle.menuGuid !== "";
        const menu = bookingResult?.menus?.find(
            (menu) => menu.publicId === updatedArticle?.menuGuid
        )?.groups;
        const menuHasItems = menu?.some((group) => group.items && group.items.length > 0);
        const menuRule = bookingResult?.webBookingRules?.find(
            (rule) => rule.id === updatedArticle?.ruleId
        );
        const menuMandatory = menuRule?.requireMenuSelection;
        setSelectedArticleHasMandatoryMenu?.(!!menuMandatory);
        if (articeHasMenu && menuHasItems) {
            setStep(1);
        } else {
            onCloseModal();
            setStep(0);
        }
    };

    // Preorder selection
    const onContinueWithoutMenu = () => {
        setOpenActivity?.(null);
        setSelectedTime(undefined);
        setStep(0);
    };

    // Ensure the latest version of preorderState is used
    const latestCurrentQuantities = useRef(currentQuantities);
    useEffect(() => {
        latestCurrentQuantities.current = currentQuantities;
    }, [currentQuantities]);

    const updateBookingMenu = useBookingMenuRespond();
    const onPreorderContinue = async () => {
        if (currentTotalQuantity && currentTotalQuantity > 0) {
            // We have to use the current timeslot start time and match it to the activity to open the correct menu
            const currentActivity = booking?.articles.find((activity) =>
                activity.start.isSame(selectedTime?.start)
            );
            const currentMenu = booking?.menus?.find(
                (menu) =>
                    menu.status === MenuStatus.Sent && menu.publicId === currentActivity?.menuGuid
            );
            if (!currentMenu?.groups || !latestCurrentQuantities.current) {
                return onContinueWithoutMenu();
            }

            const bookingMenuWithChoices: BookingMenu = {
                ...currentMenu,
                groups: currentMenu.groups.map((group, groupIndex) => ({
                    ...group,
                    items:
                        group.items?.map((item, itemIndex) => ({
                            ...item,
                            quantity:
                                latestCurrentQuantities.current?.[groupIndex]?.[itemIndex] || 0,
                        })) || [],
                })),
            };
            await updateBookingMenu.mutateAsync(bookingMenuWithChoices);
        }
        return onContinueWithoutMenu();
    };

    const imageParams: Record<string, string | undefined> = {
        AspectRatio: ImageServiceAspectRatio.SixteenByNine,
        Type: "webp",
    };
    const baseUrl = openActivity?.image
        ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${openActivity.image}`
        : "";
    const sectionImageUrl = baseUrl ? urlBuilder(baseUrl, imageParams) : "";
    const headerBackground = openActivity?.image ? sectionImageUrl : undefined;

    const bodyComponent =
        step === 0 && openActivity ? (
            <ActivityModalBody
                key={"wizardbody-0"}
                section={openActivity}
                selectedTime={selectedTime}
                setSelectedTime={handleSelectTime}
                setSelectedWaitListTimes={setWaitListTime}
                selectedWaitListTimes={selectedWaitListTimes}
            />
        ) : (
            <PreorderBody
                key={"wizardbody-1"}
                selectedTime={selectedTime}
                preorderHeaderName={openActivity?.name}
            />
        );

    const cancelBooking = useCancelBooking();
    const handleCancelBooking = async () => {
        await cancelBooking.mutateAsync({});
        queryClient.removeQueries({
            queryKey: ["booking", bookingId],
        });
        await queryClient.invalidateQueries({
            queryKey: ["booking", bookingId],
        });
        handleWaitlistContinue(true);
    };

    const onActivityContinue = selectedTime ? onActivityModalContinue : handleWaitlistContinue;
    const continueButton =
        step === 0 ? (
            <ContinueButton
                isDisabled={!selectedTime && !originalTime && selectedWaitListTimes.length === 0}
                buttonText={activityButtonText()}
                onContinue={onActivityContinue}
            />
        ) : (
            <PreorderContinueButton onContinue={onPreorderContinue} />
        );

    const tooLargeGroupSize = maxGroupSize > 0 && guests && guests > maxGroupSize;
    const openTimeSets = openActivity?.timeSets.filter((x) => !x.isTextRow && !x.isClosed);
    const allTimesUnavailable = openTimeSets?.every((timeSet) =>
        timeSet.times.every((time) => time.unavailableForGroupSize)
    );
    return (
        <>
            <Modal
                isOpen={Boolean(openActivity)}
                onClose={() => {
                    setStep(0);
                    onCloseModal();
                }}
                closeOnOverlayClick={false}
                size={modalSize ?? "md"}
                data-testid={testId.customizeWizard.modalwrapper}
            >
                <CustomizeWizardModalBody
                    onBack={step === 0 ? undefined : () => setStep(0)}
                    headerImage={step === 0 ? headerBackground : undefined}
                >
                    {bodyComponent}
                    <Box h="100vh" />
                </CustomizeWizardModalBody>
                <ModalFooter
                    zIndex={1}
                    p={ThemeSpaceVariable.Medium}
                    boxShadow={ThemeShadowVariable.Footer}
                >
                    {tooLargeGroupSize ? (
                        <Alert type="info">
                            {t("activityUnavailable", {
                                maxGuests: maxGroupSize,
                                activityName: openActivity?.name,
                            })}
                        </Alert>
                    ) : allTimesUnavailable ? (
                        <Alert type="info">
                            {t("activityFullyBooked", { activityName: openActivity?.name, guests })}
                        </Alert>
                    ) : (
                        continueButton
                    )}
                </ModalFooter>
            </Modal>
            <ConfirmationDialog
                secondaryButtonText={t("summary.cancel")}
                primaryButtonText={t("waitList.continue")}
                isOpen={isOpen}
                title={t("waitList.bookingClearWarningTitle")}
                message={t("waitList.bookingClearWarningMessage")}
                onClose={onClose}
                secondaryAction={onClose}
                primaryAction={handleCancelBooking}
            />
        </>
    );
};

export default CustomizeActivity;
